import { useEffect } from 'react';
import { resolveTheme, THEME_COLOR } from '../../core/theme';
import { useEditorStore } from '../../state/editorStore';

/**
 * Applies the chosen theme to the document.
 *
 * `data-theme` on `<html>` is the one switch: every Tailwind colour utility
 * compiles to `var(--color-…)`, so overriding the tokens under that selector
 * re-skins the shell. The `theme-color` meta follows so mobile browser chrome
 * matches instead of showing a dark bar over a light editor.
 */
export function useTheme(): void {
  const preference = useEditorStore((s) => s.theme);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const resolved = resolveTheme(preference, media.matches);
      document.documentElement.dataset.theme = resolved;
      document.documentElement.style.colorScheme = resolved;
      for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
        meta.setAttribute('content', THEME_COLOR[resolved] ?? THEME_COLOR.dark);
      }
    };
    apply();
    // Only 'system' cares what the OS is doing; the others must ignore it.
    if (preference !== 'system') return;
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [preference]);
}
